import { ReactNode, forwardRef } from 'react';
import { Icons } from '../../DataDisplay/Icon/icons';

import Icon, { Color } from '../../DataDisplay/Icon/Icon';
import Box from '../../Layout/Box/Box';

import styles from './Button.module.scss';

import { useDatadogLog } from '../logging';

export type ButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'disabled'> & {
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'outerShadow';
  color?:
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'lined'
    | 'none'
    | 'none-light'
    | 'hover'
    | 'locked'
    | 'red'
    | 'dark'
    | 'yellow'
    | string;
  size?: 'sm' | 'lg' | 'xlg' | 'jumbo';
  textColor?: string;
  textAlign?: 'start' | 'end' | 'left' | 'right' | 'center' | 'justify' | 'match-parent';
  round?: boolean;
  textSize?: number;
  fontUnit?: 'em' | 'px';
  fontWeight?: number;
  bold?: boolean;
  background?: string;
  borderColor?: string;
  width?: string;
  height?: string;
  label: ReactNode;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  showLoadingIcon?: boolean;
  noShadow?: boolean;
  noPadding?: boolean;
  hasLink?: boolean;
  isCircle?: boolean;
  hasCustomIcon?: boolean;
  customIconImage?: string;
  icon?: Icons;
  iconLocation?: 'left' | 'right' | 'start' | 'end' | 'center';
  iconSize?: 'xsm' | 'sm' | 'md' | 'xlg';
  iconWidth?: string;
  iconHeight?: string;
  iconAfterLabel?: boolean;
  isIconVisible?: boolean;
  iconColor?: Color;
  justifyLabel?: 'center' | 'fs' | 'fe' | 'sa' | 'sb' | 'se' | 'st' | undefined;
  borderRadius?: string;
  opacity?: string;
  textDecoration?: 'underline' | 'none';
  form?: string;
  className?: string;
  onClick?: () => void;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      type = 'button',
      variant = '',
      color = 'primary',
      size = 'lg',
      textColor = 'auto',
      round = false,
      textSize,
      fontUnit = 'em',
      fontWeight = 700,
      bold = true,
      label = 'Button',
      background,
      borderColor,
      width,
      height,
      isSubmitting,
      isDisabled = false,
      showLoadingIcon = true,
      noShadow = false,
      noPadding,
      hasLink,
      isCircle,
      hasCustomIcon,
      customIconImage,
      icon,
      iconLocation,
      iconSize = 'sm',
      textAlign = 'center',
      iconWidth,
      iconHeight,
      iconAfterLabel = true,
      isIconVisible = true,
      iconColor,
      justifyLabel = 'center',
      textDecoration,
      opacity,
      borderRadius,
      className,
      form,
      onClick = () => {},
      ...props
    },
    ref,
  ) => {
    const log = useDatadogLog();
    return (
      <button
        ref={ref}
        id={id}
        type={type}
        className={[
          styles.button,
          styles[size],
          styles[color],
          styles[variant],
          styles[getShadowClass()],
          styles[getBoldClass()],
          styles[getRoundClass()],
          styles[getDisabledClass()],
          styles[getPaddingClass()],
          styles[getLinkClass()],
          styles[getCircleClass()],
          className,
        ].join(' ')}
        style={{
          width,
          height,
          background,
          borderColor,
          fontSize: `${String(textSize)}${fontUnit === 'em' ? 'em' : 'px'}`,
          color: `${textColor}`,
          opacity,
          textAlign,
          fontWeight,
          textDecoration,
          borderRadius,
        }}
        form={form}
        onClick={handleClick}
        disabled={isDisabled || isSubmitting}
        data-custom="true"
        {...props}
        data-testid={id}
      >
        <Box jc={justifyLabel} ai="center" position="relative">
          {isIconVisible && iconLocation === 'left' && getIcon('left')}
          {isIconVisible && !iconAfterLabel && iconLocation === 'center' && getIcon('center')}
          {isIconVisible && isSubmitting && showLoadingIcon && (
            <Icon icon="loader" height="37px" width="37px" color={getIconColor()} />
          )}
          {label}
          {isIconVisible && iconLocation === 'right' && getIcon('right')}
          {isIconVisible && iconLocation === 'start' && getIcon('start')}
          {isIconVisible && iconLocation === 'end' && getIcon('end')}
          {isIconVisible && iconAfterLabel && iconLocation === 'center' && getIcon('center')}
        </Box>
      </button>
    );

    function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
      log({
        message: `${id || 'Button'} clicked`,
        payload: { name: id },
        actionType: 'Button',
      });

      if (onClick) {
        onClick(e);
      }
    }

    function getDisabledClass() {
      return isSubmitting || isDisabled ? 'disabled' : '';
    }

    function getShadowClass() {
      return noShadow ? 'noShadow' : '';
    }

    function getBoldClass() {
      return bold ? 'bold' : 'normal';
    }

    function getRoundClass() {
      return round ? 'round' : '';
    }

    function getIconColor() {
      return textColor === '#FBFBFB' ? 'medium-light' : 'primary-dark';
    }

    function getPaddingClass() {
      return noPadding ? 'noPadding' : '';
    }

    function getLinkClass() {
      return hasLink ? 'link' : '';
    }

    function getCircleClass() {
      return isCircle ? 'circle' : '';
    }

    function getIcon(position: 'left' | 'right' | 'start' | 'end' | 'center') {
      const colorMap: { [key: string]: Color } = {
        primary: 'primary-dark',
        red: 'primary-light',
        secondary: 'primary-dark',
        mediumGrey: 'medium-grey',
        none: 'primary-dark',
        'none-light': 'primary-light',
        grey: 'grey',
      };
      return hasCustomIcon && customIconImage !== '' ? (
        <img className={styles[`icon--${colorMap[color]}`]} src={customIconImage} alt={`''`} />
      ) : (
        <Icon
          icon={icon}
          color={isDisabled ? 'grey' : iconColor || colorMap[color]}
          position={position}
          size={iconSize}
          height={iconHeight}
          width={iconWidth}
        />
      );
    }
  },
);

Button.displayName = 'Button';

export default Button;
